import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { FlatpickrModule } from 'angularx-flatpickr';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

export interface CardPeriodOption {
  value: string;
  label: string;
}

const DEFAULT_PERIOD_OPTIONS: CardPeriodOption[] = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'quarterly', label: 'Quarterly' },
];

/** Built-in icons for the card heading (add more keys + template cases as needed). */
export type CardHeadingIcon =
  | 'chart'
  | 'users'
  | 'layout'
  | 'bar-chart'
  | 'cpu'
  | 'notes'
  | 'none'
  | 'txt'
  | 'clipboard'
  | 'list';

@Component({
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, FlatpickrModule, IconModule, MaterialModule],
  selector: 'app-card-title',
  templateUrl: './card-title.component.html',
})
export class CardTitleComponent {
  @Input() cardHead: string = '';
  /** Which icon appears before the title. Use `'none'` to hide the icon (or set `showHeadingIcon` to false). */
  @Input() headingIcon: CardHeadingIcon = 'chart';
  /** When false, no heading icon is shown (overrides `headingIcon`). */
  @Input() showHeadingIcon = true;

  /** Shared Tailwind classes passed to the SVG icon components. */
  readonly headingIconClass = 'size-5 shrink-0 text-dark dark:text-white';
  /** When true, shows the Monthly / Yearly dropdown. Default false so titles stay title-only unless opted in. */
  @Input() showPeriodSelect = false;
  /** Selected option `value`. Must match one of `periodOptions` (or the default monthly/yearly/quarterly list). */
  @Input() period: string = 'monthly';
  /** Override dropdown rows (labels + values). If omitted, uses Monthly / Yearly / Quarterly. */
  @Input() periodOptions: CardPeriodOption[] | null = null;
  @Output() periodChange = new EventEmitter<string>();

  periodDropdownOpen = false;

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  get effectivePeriodOptions(): CardPeriodOption[] {
    return this.periodOptions?.length ? this.periodOptions : DEFAULT_PERIOD_OPTIONS;
  }

  get periodLabel(): string {
    return (
      this.effectivePeriodOptions.find((o) => o.value === this.period)?.label ??
      this.effectivePeriodOptions[0]?.label ??
      ''
    );
  }

  togglePeriodDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.periodDropdownOpen = !this.periodDropdownOpen;
  }

  selectPeriod(value: string, event: MouseEvent): void {
    event.stopPropagation();
    this.period = value;
    this.periodChange.emit(value);
    this.periodDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.periodDropdownOpen || !this.showPeriodSelect) {
      return;
    }
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.periodDropdownOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.periodDropdownOpen) {
      this.periodDropdownOpen = false;
    }
  }
}
